import { useCallback, useEffect, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { m } from "framer-motion";
import {
  AlertCircle,
  Building2,
  Check,
  CheckCircle2,
  Clock,
  Loader2,
  MinusCircle,
  Plus,
  Search,
  Send,
  X,
  XCircle,
} from "lucide-react";

import { showToast } from "@/lib/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import {
  SwipeableTabsContent,
  type SwipeableTabsHandle,
} from "@/components/ui/swipeable-tabs-content";
import { TabBar, type TabItem } from "@/components/ui/tab-bar";

export type AffiliationsTab = "pending" | "active" | "history";
export const AFFILIATIONS_VALID_TABS: readonly AffiliationsTab[] = [
  "pending",
  "active",
  "history",
] as const;

interface Affiliation {
  id: string;
  facilityId: string;
  facilityName: string;
  role: string;
  status: string;
  initiatedBy: string;
  specialtyHere: string | null;
  invitedAt: string;
  respondedAt: string | null;
  startDate: string | null;
}

interface FacilityResult {
  id: string;
  name: string;
  city?: string;
  photoUrl?: string;
  providerType: string;
}

interface GeoResult {
  formattedAddress: string;
  lat: number;
  lng: number;
  street: string;
  city: string;
  county: string;
  country: string;
  postalCode: string;
}

interface AffiliationsClientProps {
  activeTab: AffiliationsTab;
  onTabChange: (tab: AffiliationsTab) => void;
  /** When true, renders the page-level header (h1 + subtitle + CTA on
   *  the same row). When false, the host (e.g. settings hub tab) owns
   *  the header and the CTA renders above the tab bar instead. */
  showHeader?: boolean;
}

/**
 * Provider facility-affiliations surface.
 *
 * 1:1 port of legacy `affiliations-client.tsx` (608 LOC):
 *   - 3 tabs (pending/active/history) with count badge on each
 *   - Per-affiliation card with status pill + role chip + initiator-aware
 *     actions (facility-pending → Accept/Decline; provider-pending → Cancel)
 *   - Request-to-join sheet: debounced facility search (300ms,
 *     /api/search?providerType=facility&limit=5) + role picker
 *     (collaborator/employed) + 500-char message + submit
 *
 * Convention adaptations for apps/app:
 *   - useTranslations → useLingui + <Trans>
 *   - useRouter (next) — not used here (tab state is parent-owned via props)
 *   - apiFetch from @pontimed/api-client (not @/lib/api/client)
 *   - cn from @pontimed/ui
 *   - Dialog → <ResponsiveSheet> (mandatory primitive)
 *   - showToast from @/lib/ui/toast (success/error pair, mirrors legacy)
 *   - Tab state owned by parent (route OR settings hub)
 *
 * Endpoints:
 *   GET    /api/account/provider/affiliations
 *   PATCH  /api/account/facility-providers       (accept/decline by facility-initiated)
 *   DELETE /api/account/provider/facilities?facilityId=... (cancel provider-initiated)
 *   POST   /api/account/provider/request-affiliation
 *   GET    /api/search?q=&providerType=facility&limit=5
 */
export function AffiliationsClient({
  activeTab,
  onTabChange,
  showHeader = true,
}: AffiliationsClientProps) {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const swipeRef = useRef<SwipeableTabsHandle>(null);

  const [affiliations, setAffiliations] = useState<Affiliation[]>([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Request-to-join state
  const [requestOpen, setRequestOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<FacilityResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<FacilityResult | null>(null);
  const [selectedRole, setSelectedRole] = useState("collaborator");
  const [requestMessage, setRequestMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // "Can't find it?" branches — create own facility / suggest missing clinic
  const [extraMode, setExtraMode] = useState<null | "create" | "suggest">(null);
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState<"private_practice" | "clinic">("private_practice");
  const [addrQuery, setAddrQuery] = useState("");
  const [addrResults, setAddrResults] = useState<GeoResult[]>([]);
  const [addrSearching, setAddrSearching] = useState(false);
  const [selectedAddr, setSelectedAddr] = useState<GeoResult | null>(null);
  const [sgName, setSgName] = useState("");
  const [sgAddress, setSgAddress] = useState("");
  const [sgPhone, setSgPhone] = useState("");
  const [sgEmail, setSgEmail] = useState("");

  const resetSheet = useCallback(() => {
    setRequestOpen(false);
    setSelectedFacility(null);
    setSearchQuery("");
    setSelectedRole("collaborator");
    setRequestMessage("");
    setExtraMode(null);
    setNewName("");
    setNewCategory("private_practice");
    setAddrQuery("");
    setAddrResults([]);
    setSelectedAddr(null);
    setSgName("");
    setSgAddress("");
    setSgPhone("");
    setSgEmail("");
  }, []);

  const fetchAffiliations = useCallback(async () => {
    try {
      const data = await apiFetch.get<{ affiliations: Affiliation[] }>(
        "/api/account/provider/affiliations",
      );
      setAffiliations(data.affiliations ?? []);
      setErrored(false);
    } catch (err) {
      // Treat any error (incl. 403) as load failure — show error block
      if (err instanceof ApiError) setErrored(true);
      else setErrored(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAffiliations();
  }, [fetchAffiliations]);

  // Debounced facility search — only when sheet is open and query >= 2 chars.
  useEffect(() => {
    if (!requestOpen) return;
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        // /api/search answers `{ hits, facilities, total, facets }` — there is
        // no `results` key, so this sheet always showed an empty list and a
        // provider could never pick an existing facility to request joining.
        // Facility docs in the providers index are prefixed "fac_<uuid>".
        const data = await apiFetch.get<{ hits?: Record<string, unknown>[] }>(
          // includeEmpty: the default search filter hides facilities with no
          // provider on the platform yet — exactly the clinics a first doctor
          // wants to request joining.
          `/api/search?q=${encodeURIComponent(searchQuery)}&providerType=facility&includeEmpty=true&limit=5`,
        );
        setSearchResults(
          (data.hits ?? []).map((h) => ({
            id: ((h.id as string) || "").replace(/^fac_/, ""),
            name: (h.name as string) || "",
            city: (h.city as string) || (h.address as string) || "",
            photoUrl: (h.photoUrl as string) || (h.logoUrl as string) || undefined,
            providerType: (h.providerType as string) || "facility",
          })),
        );
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, requestOpen]);

  const handleAction = async (id: string, action: "accept" | "decline") => {
    setActionLoading(id);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await apiFetch.patch("/api/account/facility-providers", { id, action });
      await fetchAffiliations();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("account-state-changed"));
      }
      toast.update(t`Saved`, "success");
    } catch {
      toast.update(t`Could not save. Try again.`, "error");
    } finally {
      setActionLoading(null);
    }
  };

  // Cancel a self-initiated affiliation request. Uses the provider's own
  // facilities DELETE endpoint, which marks the row status="declined" so it
  // drops out of both the active+pending locations list and the pending tab.
  const handleCancel = async (aff: Affiliation) => {
    setActionLoading(aff.id);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await apiFetch.raw(
        `/api/account/provider/facilities?facilityId=${encodeURIComponent(aff.facilityId)}`,
        { method: "DELETE" },
      );
      await fetchAffiliations();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("account-state-changed"));
      }
      toast.update(t`Saved`, "success");
    } catch {
      toast.update(t`Could not save. Try again.`, "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRequestAffiliation = async () => {
    if (!selectedFacility) return;
    setSubmitting(true);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await apiFetch.post("/api/account/provider/request-affiliation", {
        facilityId: selectedFacility.id,
        role: selectedRole,
        message: requestMessage.trim() || undefined,
      });
      resetSheet();
      await fetchAffiliations();
      toast.update(t`Saved`, "success");
    } catch {
      toast.update(t`Could not save. Try again.`, "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Debounced address geocode — only while the create-own-facility form is open.
  useEffect(() => {
    if (extraMode !== "create") return;
    if (!addrQuery || addrQuery.trim().length < 3 || selectedAddr) {
      setAddrResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setAddrSearching(true);
      try {
        const data = await apiFetch.get<{ results: GeoResult[] }>(
          `/api/geocode?q=${encodeURIComponent(addrQuery)}`,
        );
        setAddrResults(data.results ?? []);
      } catch {
        setAddrResults([]);
      } finally {
        setAddrSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [addrQuery, extraMode, selectedAddr]);

  const handleCreateFacility = async () => {
    if (newName.trim().length < 2 || !selectedAddr) return;
    setSubmitting(true);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await apiFetch.post("/api/account/provider/facilities", {
        facilityId: "new",
        newFacility: {
          name: { ro: newName.trim(), en: newName.trim() },
          addressStreet: selectedAddr.street || null,
          addressCity: selectedAddr.city || null,
          addressCounty: selectedAddr.county || null,
          addressPostal: selectedAddr.postalCode || null,
          addressCountry: selectedAddr.country || undefined,
          addressFormatted: selectedAddr.formattedAddress || null,
          latitude: selectedAddr.lat,
          longitude: selectedAddr.lng,
          category: newCategory,
        },
      });
      resetSheet();
      await fetchAffiliations();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("account-state-changed"));
      }
      toast.update(t`Facility created`, "success");
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        toast.update(t`Facility limit reached on your plan — upgrade to add more.`, "error");
      } else {
        toast.update(t`Could not save. Try again.`, "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuggestClinic = async () => {
    if (sgName.trim().length < 2) return;
    setSubmitting(true);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await apiFetch.post("/api/account/provider/suggest-clinic", {
        clinicName: sgName.trim(),
        clinicAddress: sgAddress.trim() || undefined,
        clinicPhone: sgPhone.trim() || undefined,
        clinicEmail: sgEmail.trim() || undefined,
      });
      resetSheet();
      toast.update(t`Thanks — our team will reach out to them.`, "success");
    } catch {
      toast.update(t`Could not save. Try again.`, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const buckets = {
    pending: affiliations.filter((a) => a.status === "pending"),
    active: affiliations.filter((a) => a.status === "active"),
    history: affiliations.filter((a) => ["declined", "removed"].includes(a.status)),
  };

  const tabs: TabItem<AffiliationsTab>[] = (
    [
      { id: "pending" as const, label: t`Pending`, count: buckets.pending.length },
      { id: "active" as const, label: t`Active`, count: buckets.active.length },
      { id: "history" as const, label: t`History`, count: buckets.history.length },
    ] satisfies { id: AffiliationsTab; label: string; count: number }[]
  ).map(
    (tab): TabItem<AffiliationsTab> => ({
      id: tab.id,
      label: tab.label,
      trailing:
        tab.count > 0 ? (
          <span
            className={cn(
              "min-w-[20px] rounded-full px-1.5 py-0.5 text-center text-[0.625rem] font-semibold",
              activeTab === tab.id
                ? "bg-primary/15 text-primary"
                : "bg-muted text-muted-foreground",
            )}
          >
            {tab.count}
          </span>
        ) : undefined,
    }),
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {showHeader && (
        <header className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
              <Trans>Facility affiliations</Trans>
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              <Trans>Facilities you practice at — invitations, active links, and history.</Trans>
            </p>
          </div>
          <Button onClick={() => setRequestOpen(true)} className="gap-2" size="sm">
            <Plus className="size-4" />
            <span className="hidden sm:inline">
              <Trans>Request to join</Trans>
            </span>
          </Button>
        </header>
      )}

      {!showHeader && (
        <div className="flex justify-end">
          <Button onClick={() => setRequestOpen(true)} className="gap-2" size="sm">
            <Plus className="size-4" />
            <Trans>Request to join</Trans>
          </Button>
        </div>
      )}

      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        onActiveTabClick={() => swipeRef.current?.scrollActiveToTop()}
      />

      <SwipeableTabsContent
        ref={swipeRef}
        activeTab={activeTab}
        tabIds={AFFILIATIONS_VALID_TABS}
        onTabChange={onTabChange}
        renderPanel={(panelKey) => {
          if (loading)
            return (
              <div className="space-y-3 pb-6">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="border-border bg-card animate-pulse rounded-xl border p-5"
                  >
                    <div className="flex items-center gap-4">
                      <div className="bg-muted size-12 rounded-xl" />
                      <div className="flex-1 space-y-2">
                        <div className="bg-muted h-4 w-40 rounded" />
                        <div className="bg-muted h-3 w-24 rounded" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          if (errored)
            return (
              <div className="border-destructive/40 bg-destructive/5 rounded-xl border p-6 text-center">
                <AlertCircle className="text-destructive mx-auto size-8" />
                <p className="text-destructive mt-3 text-sm font-medium">
                  <Trans>Could not load affiliations.</Trans>
                </p>
              </div>
            );
          const items = buckets[panelKey];
          if (items.length === 0)
            return (
              <div className="border-border bg-card flex flex-col items-center justify-center rounded-xl border py-16 text-center">
                <Building2 className="text-muted-foreground size-10" />
                <p className="text-muted-foreground mt-3 text-sm">
                  {panelKey === "pending" ? (
                    <Trans>No pending affiliations.</Trans>
                  ) : panelKey === "active" ? (
                    <Trans>You're not affiliated with any facility yet.</Trans>
                  ) : (
                    <Trans>No history yet.</Trans>
                  )}
                </p>
              </div>
            );
          return (
            <div className="space-y-3 pb-6">
              {items.map((aff, idx) => (
                <AffiliationCard
                  key={aff.id}
                  aff={aff}
                  index={idx}
                  locale={locale}
                  actionLoading={actionLoading}
                  onAccept={() => handleAction(aff.id, "accept")}
                  onDecline={() => handleAction(aff.id, "decline")}
                  onCancel={() => handleCancel(aff)}
                />
              ))}
            </div>
          );
        }}
      />

      {/* Request-to-join sheet — bottom-sheet on mobile, centered dialog on desktop */}
      <ResponsiveSheet
        open={requestOpen}
        onClose={resetSheet}
        title={<Trans>Request to join a facility</Trans>}
        description={
          <Trans>
            Search for the facility, pick your role, and send a short message — they'll review and
            respond from their dashboard.
          </Trans>
        }
        width="md"
      >
        <div className="space-y-4">
          {/* Search facilities */}
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedFacility(null);
              }}
              placeholder={t`Search by facility name…`}
              className="pl-10"
            />
          </div>

          {searching && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="text-muted-foreground size-5 animate-spin" />
            </div>
          )}

          {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
            <p className="text-muted-foreground py-4 text-center text-sm">
              <Trans>No facilities found. Try a different search.</Trans>
            </p>
          )}

          {!searching && searchResults.length > 0 && !selectedFacility && (
            <div className="max-h-60 space-y-2 overflow-y-auto">
              {searchResults.map((result) => (
                <button
                  key={result.id}
                  type="button"
                  onClick={() => setSelectedFacility(result)}
                  className="border-border bg-card hover:bg-muted w-full rounded-lg border p-3 text-left transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-lg">
                      <Building2 className="text-muted-foreground size-5" />
                    </div>
                    <div>
                      <p className="text-foreground text-sm font-medium">{result.name}</p>
                      {result.city && (
                        <p className="text-muted-foreground text-xs">{result.city}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedFacility && (
            <m.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-muted/50 border-border space-y-3 rounded-lg border p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="text-muted-foreground size-4" />
                  <p className="text-foreground text-sm font-medium">{selectedFacility.name}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedFacility(null)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={t`Clear selection`}
                >
                  <X className="size-4" />
                </button>
              </div>

              <div>
                <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
                  <Trans>Role</Trans>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "collaborator", label: <Trans>Collaborator</Trans> },
                    { value: "employed", label: <Trans>Employed</Trans> },
                  ].map((r) => (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setSelectedRole(r.value)}
                      className={cn(
                        "min-h-[44px] rounded-lg border px-3 py-2 text-xs font-medium transition-colors",
                        selectedRole === r.value
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:bg-muted",
                      )}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
                  <Trans>Message (optional)</Trans>
                </label>
                <textarea
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  placeholder={t`Hi, I'd like to join your facility — quick intro about me and what I do.`}
                  maxLength={500}
                  rows={3}
                  className="border-border bg-card text-foreground placeholder:text-muted-foreground focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
                />
                <p className="text-muted-foreground mt-1 text-right text-[0.625rem]">
                  {requestMessage.length}/500
                </p>
              </div>

              <Button
                onClick={handleRequestAffiliation}
                disabled={submitting}
                className="min-h-[44px] w-full gap-2"
              >
                {submitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
                <Trans>Send request</Trans>
              </Button>
            </m.div>
          )}

          {/* "Can't find it?" — create your own facility or suggest a missing clinic */}
          {!selectedFacility && (
            <div className="border-border space-y-2 border-t pt-3">
              <p className="text-muted-foreground text-xs font-medium">
                <Trans>Can't find the facility?</Trans>
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setExtraMode(extraMode === "create" ? null : "create")}
                  className={cn(
                    "flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-colors",
                    extraMode === "create"
                      ? "border-primary bg-primary-soft text-primary"
                      : "border-border text-muted-foreground hover:bg-muted",
                  )}
                >
                  <Plus className="size-4 shrink-0" />
                  <Trans>Add a facility you own</Trans>
                </button>
                <button
                  type="button"
                  onClick={() => setExtraMode(extraMode === "suggest" ? null : "suggest")}
                  className={cn(
                    "flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-colors",
                    extraMode === "suggest"
                      ? "border-primary bg-primary-soft text-primary"
                      : "border-border text-muted-foreground hover:bg-muted",
                  )}
                >
                  <Send className="size-4 shrink-0" />
                  <Trans>Suggest a missing clinic</Trans>
                </button>
              </div>

              {extraMode === "create" && (
                <m.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-muted/50 border-border space-y-3 rounded-lg border p-4"
                >
                  <div>
                    <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
                      <Trans>Facility name</Trans>
                    </label>
                    <Input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder={t`e.g. Cabinet Dr. Popescu`}
                    />
                  </div>

                  <div>
                    <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
                      <Trans>Type</Trans>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {(
                        [
                          { value: "private_practice", label: <Trans>Private practice</Trans> },
                          { value: "clinic", label: <Trans>Clinic</Trans> },
                        ] as const
                      ).map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setNewCategory(opt.value)}
                          className={cn(
                            "min-h-[44px] rounded-lg border px-3 py-2 text-xs font-medium transition-colors",
                            newCategory === opt.value
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border text-muted-foreground hover:bg-muted",
                          )}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
                      <Trans>Address</Trans>
                    </label>
                    <Input
                      value={selectedAddr ? selectedAddr.formattedAddress : addrQuery}
                      onChange={(e) => {
                        setAddrQuery(e.target.value);
                        setSelectedAddr(null);
                      }}
                      placeholder={t`Street, number, city…`}
                    />
                    {addrSearching && (
                      <div className="flex items-center justify-center py-2">
                        <Loader2 className="text-muted-foreground size-4 animate-spin" />
                      </div>
                    )}
                    {!selectedAddr && addrResults.length > 0 && (
                      <div className="border-border bg-card mt-1 max-h-40 space-y-0.5 overflow-y-auto rounded-lg border p-1">
                        {addrResults.map((r, i) => (
                          <button
                            key={`${r.lat}-${r.lng}-${i}`}
                            type="button"
                            onClick={() => {
                              setSelectedAddr(r);
                              setAddrResults([]);
                            }}
                            className="hover:bg-muted w-full rounded-md px-2 py-2 text-left text-xs"
                          >
                            {r.formattedAddress}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleCreateFacility}
                    disabled={submitting || newName.trim().length < 2 || !selectedAddr}
                    className="min-h-[44px] w-full gap-2"
                  >
                    {submitting ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Plus className="size-4" />
                    )}
                    <Trans>Create facility</Trans>
                  </Button>
                  <p className="text-muted-foreground text-[0.625rem] leading-snug">
                    <Trans>
                      You'll be the owner. You can fine-tune the address pin, schedule, and services
                      from the facility settings afterwards.
                    </Trans>
                  </p>
                </m.div>
              )}

              {extraMode === "suggest" && (
                <m.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-muted/50 border-border space-y-3 rounded-lg border p-4"
                >
                  <p className="text-muted-foreground text-xs leading-snug">
                    <Trans>
                      Know a clinic that should be on PontiMed? Leave their details and our team
                      will reach out to them.
                    </Trans>
                  </p>
                  <Input
                    value={sgName}
                    onChange={(e) => setSgName(e.target.value)}
                    placeholder={t`Clinic name (required)`}
                  />
                  <Input
                    value={sgAddress}
                    onChange={(e) => setSgAddress(e.target.value)}
                    placeholder={t`Address or city (optional)`}
                  />
                  <Input
                    value={sgPhone}
                    onChange={(e) => setSgPhone(e.target.value)}
                    placeholder={t`Phone (optional)`}
                    type="tel"
                  />
                  <Input
                    value={sgEmail}
                    onChange={(e) => setSgEmail(e.target.value)}
                    placeholder={t`Email (optional)`}
                    type="email"
                  />
                  <Button
                    onClick={handleSuggestClinic}
                    disabled={submitting || sgName.trim().length < 2}
                    className="min-h-[44px] w-full gap-2"
                  >
                    {submitting ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Send className="size-4" />
                    )}
                    <Trans>Send suggestion</Trans>
                  </Button>
                </m.div>
              )}
            </div>
          )}
        </div>
      </ResponsiveSheet>
    </div>
  );
}

// Known affiliation roles → localized labels (legacy roleOwner/roleEmployed/
// roleCollaborator/roleResident keys). Unknown roles fall back to the raw value.
const ROLE_LABELS: Record<string, React.ReactNode> = {
  owner: <Trans>Owner</Trans>,
  employed: <Trans>Employed</Trans>,
  collaborator: <Trans>Collaborator</Trans>,
  resident: <Trans>Resident</Trans>,
};

function AffiliationCard({
  aff,
  index,
  locale,
  actionLoading,
  onAccept,
  onDecline,
  onCancel,
}: {
  aff: Affiliation;
  index: number;
  locale: string;
  actionLoading: string | null;
  onAccept: () => void;
  onDecline: () => void;
  onCancel: () => void;
}) {
  const isPendingFromFacility = aff.status === "pending" && aff.initiatedBy === "facility";
  const isPendingFromProvider = aff.status === "pending" && aff.initiatedBy === "provider";
  const invited = new Date(aff.invitedAt).toLocaleDateString(locale);
  const responded = aff.respondedAt ? new Date(aff.respondedAt).toLocaleDateString(locale) : null;
  const busy = actionLoading === aff.id;

  return (
    <m.article
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="border-border bg-card rounded-xl border p-4 sm:p-5"
    >
      <div className="flex items-start gap-4">
        <div
          className={cn(
            "flex size-12 shrink-0 items-center justify-center rounded-xl",
            aff.status === "pending"
              ? "bg-amber-100 dark:bg-amber-950"
              : aff.status === "active"
                ? "bg-emerald-100 dark:bg-emerald-950"
                : "bg-muted",
          )}
        >
          <Building2
            className={cn(
              "size-6",
              aff.status === "pending"
                ? "text-amber-600 dark:text-amber-400"
                : aff.status === "active"
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-muted-foreground",
            )}
          />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-foreground truncate text-base font-semibold">{aff.facilityName}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
            <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs font-medium capitalize">
              {ROLE_LABELS[aff.role] ?? aff.role.replace("_", " ")}
            </span>
            <StatusBadge status={aff.status} />
            {isPendingFromProvider && (
              <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                <Send className="size-3" />
                <Trans>Request sent</Trans>
              </span>
            )}
          </div>
          {aff.specialtyHere && (
            <p className="text-muted-foreground mt-1 text-xs">{aff.specialtyHere}</p>
          )}
          <p className="text-muted-foreground mt-1 text-xs">
            {aff.initiatedBy === "provider" ? (
              <Trans>Requested {invited}</Trans>
            ) : (
              <Trans>Invited {invited}</Trans>
            )}
            {responded && (
              <>
                {" · "}
                <Trans>Responded {responded}</Trans>
              </>
            )}
          </p>
        </div>

        {/* Actions — accept/decline for facility invitations,
            cancel for provider-initiated requests. */}
        {isPendingFromFacility && (
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={onAccept}
              disabled={busy}
              className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg px-3 text-sm font-medium transition-colors disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <>
                  <Check className="size-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">
                    <Trans>Accept</Trans>
                  </span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onDecline}
              disabled={busy}
              className="border-border text-muted-foreground hover:text-foreground hover:bg-muted inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border px-3 text-sm font-medium transition-colors disabled:opacity-50"
            >
              <X className="size-4 sm:mr-1.5" />
              <span className="hidden sm:inline">
                <Trans>Decline</Trans>
              </span>
            </button>
          </div>
        )}
        {isPendingFromProvider && (
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="border-border text-muted-foreground hover:text-destructive hover:border-destructive/40 hover:bg-destructive/5 inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-lg border px-3 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <>
                <X className="size-4 sm:mr-1.5" />
                <span className="hidden sm:inline">
                  <Trans>Cancel request</Trans>
                </span>
              </>
            )}
          </button>
        )}
      </div>
    </m.article>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { icon: React.ReactNode; label: React.ReactNode; tone: string }> = {
    pending: {
      icon: <Clock className="size-3.5" />,
      label: <Trans>Pending</Trans>,
      tone: "text-amber-600 dark:text-amber-400",
    },
    active: {
      icon: <CheckCircle2 className="size-3.5" />,
      label: <Trans>Active</Trans>,
      tone: "text-emerald-600 dark:text-emerald-400",
    },
    declined: {
      icon: <XCircle className="size-3.5" />,
      label: <Trans>Declined</Trans>,
      tone: "text-red-500 dark:text-red-400",
    },
    removed: {
      icon: <MinusCircle className="size-3.5" />,
      label: <Trans>Removed</Trans>,
      tone: "text-muted-foreground",
    },
  };
  const cfg = config[status] ?? config.pending;
  return (
    <span className={cn("inline-flex items-center gap-1 text-xs", cfg.tone)}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
}
